const API_BASE = "/api";
const TOKEN_KEY = "vorks.token";
const REFRESH_KEY = "vorks.refresh";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}
export function setRefreshToken(token) {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

export function clearAuth() {
  setToken(null);
  setRefreshToken(null);
}

// ── Refresh logic ──
// Coalesce concurrent refreshes so only one network call happens even if
// multiple 401-triggered requests fire simultaneously.
let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const rt = getRefreshToken();
  if (!rt) {
    return Promise.reject(new Error("no_refresh_token"));
  }

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        clearAuth();
        throw new Error(data?.error || "refresh_failed");
      }
      setToken(data.token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      return data.token;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Detect JWT expiry on the client (rough — doesn't verify signature)
function tokenExpiresSoon(token) {
  if (!token) return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    const expiresInMs = payload.exp * 1000 - Date.now();
    return expiresInMs < 30_000; // refresh 30s before expiry
  } catch {
    return false;
  }
}

export async function api(path, { method = "GET", body, auth = true } = {}) {
  // Proactive refresh if access token is about to expire
  if (auth && getRefreshToken()) {
    const t = getToken();
    if (tokenExpiresSoon(t)) {
      try { await refreshAccessToken(); } catch { /* fall through */ }
    }
  }

  const doFetch = async () => {
    const headers = { "Content-Type": "application/json" };
    if (auth) {
      const t = getToken();
      if (t) headers.Authorization = `Bearer ${t}`;
    }
    return fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await doFetch();

  // Reactive refresh: if server returned 401, try to refresh and retry once
  if (res.status === 401 && auth && getRefreshToken() && path !== "/auth/refresh") {
    try {
      await refreshAccessToken();
      res = await doFetch();
    } catch {
      // Refresh failed — original 401 stays
    }
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.details = data;
    if (res.status === 401 && auth && path !== "/auth/login" && path !== "/auth/refresh") {
      clearAuth();
      window.dispatchEvent(new CustomEvent("vorks:auth-expired"));
    }
    throw err;
  }
  return data;
}
